import { Dispatch, SetStateAction, memo, useEffect } from 'react';

import { Button, Checkbox, Flex, Group, Modal, Select, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useCreateTypeMutation, useUpdateTypeMutation } from '@/api/booking-object-types';

import { Type } from '@/types/person';

import { IconX } from '@tabler/icons-react';

interface ObjectTypeFormProps {
	isOpen: boolean;
	objectType: Type | null;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	handleClose: () => void;
}

function ObjectTypeForm({ isOpen, objectType, setIsOpen, handleClose }: ObjectTypeFormProps) {
	const [updateType, { isLoading: isUpdateLoading }] = useUpdateTypeMutation();
	const [createType, { isLoading: isCreateLoading }] = useCreateTypeMutation();

	const form = useForm({
		initialValues: {
			id: objectType?.id ?? 0,
			name: objectType?.name ?? '',
			description: objectType?.description ?? '',
			kind: objectType?.kind ?? '',
			is_bookable: objectType?.is_bookable ?? false,
			is_communal: objectType?.is_communal ?? false,
		},
		validate: {
			name: isNotEmpty('Введите название'),
			description: isNotEmpty('Введите описание'),
			kind: isNotEmpty('Выберите тип'),
		},
	});

	const handleSubmit = async (data: Type) => {
		try {
			if (objectType) {
				await updateType(data);
				setIsOpen(false);
				return;
			}
			await createType(data);
			setIsOpen(false);
		} catch (e) {
			notifications.show({
				title: 'Ошибка',
				message: 'Не удалось сохранить изменения',
				color: 'red',
				icon: <IconX />,
			});
		}
	};

	useEffect(() => {
		form.setValues({
			id: objectType?.id ?? 0,
			name: objectType?.name ?? '',
			description: objectType?.description ?? '',
			kind: objectType?.kind ?? '',
			is_bookable: objectType?.is_bookable ?? false,
			is_communal: objectType?.is_communal ?? false,
		});
	}, [objectType]);

	return (
		<Modal
			title={`${objectType ? 'Изменение' : 'Создание'} пропуска`}
			opened={isOpen}
			onClose={() => {
				handleClose();
				form.reset();
			}}
			centered
			size="md"
		>
			<form
				onSubmit={form.onSubmit((values) => {
					handleSubmit(values);
					form.reset();
				})}
			>
				<Flex direction="column" gap="md">
					<TextInput
						label="Название"
						placeholder="Введите название объекта"
						withAsterisk
						{...form.getInputProps('name')}
					/>
					<TextInput
						label="Описание"
						placeholder="Введите описание объекта"
						withAsterisk
						{...form.getInputProps('description')}
					/>
					<Select
						label="Название"
						w="100%"
						data={[
							{ value: 'location', label: 'location' },
							{ value: 'parking_place', label: 'parking_place' },
							{ value: 'work_place', label: 'work_place' },
							{ value: 'work_object', label: 'work_object' },
							{ value: 'meeting_room', label: 'meeting_room' },
							{ value: 'terminal', label: 'terminal' },
						]}
						placeholder="Выберите тип объекта"
						allowDeselect={false}
						withAsterisk
						{...form.getInputProps('kind')}
					/>
					<Checkbox
						label="Доступность бронирования"
						{...form.getInputProps('is_bookable', { type: 'checkbox' })}
					/>
					<Checkbox
						label="Возможность бронирования многими"
						{...form.getInputProps('is_communal', { type: 'checkbox' })}
					/>
					<Group gap="md">
						<Button radius="sm" type="submit">
							ОК
						</Button>
						<Button
							radius="sm"
							color="red"
							onClick={() => {
								handleClose();
								form.reset();
							}}
						>
							Отмена
						</Button>
					</Group>
				</Flex>
			</form>
		</Modal>
	);
}

export default memo(ObjectTypeForm);
